import React, { useState } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Grid,
  Button,
} from "@mui/material";
import SubfolderList from "./SubfolderList";
import FileList from "./FileList";
import AnimatedText from "../../user/file_management/component/AnimatedText";
import {
  useApprovedFolders,
  useApprovedSubfolders,
  useApprovedFiles,
} from "./hooks/useApprovedFileHierarchy";
import { useAuth } from "../../../context/AuthContext";
import SummaryDrawer from "./SummaryDrawer";
import FilePreview from "./FilePreview";
import BreadcrumbNavigation from "./component/BreadcrumbNavigation";
import { downloadApprovedFolderAsZip } from "./services/downloadApprovedFolder";

function ApprovedSubmission() {
  const { currentUser } = useAuth();
  const adminId = currentUser?.uid;

  const [selectedFolder, setSelectedFolder] = useState(null);
  const [selectedSubfolder, setSelectedSubfolder] = useState(null);
  const [activeBreadcrumb, setActiveBreadcrumb] = useState(1);
  const [isDownloading, setIsDownloading] = useState(false);

  //For Opening File Preview
  const [previewFile, setPreviewFile] = useState(null);
  const handleViewFile = (file) => {
    setPreviewFile(file);
  };
  const handleClosePreview = () => {
    setPreviewFile(null);
  };
  //Opening the summary in file preview state
  const [summaryOpen, setSummaryOpen] = useState(false);
  const handleToggleSummary = () => setSummaryOpen((open) => !open);

  const {
    data: approvedFolders,
    isLoading: foldersLoading,
    error: foldersError,
  } = useApprovedFolders(adminId);

  const {
    data: subfolders,
    isLoading: subfoldersLoading,
    error: subfoldersError,
  } = useApprovedSubfolders(adminId, selectedFolder?.id);

  const {
    data: files,
    isLoading: filesLoading,
    error: filesError,
  } = useApprovedFiles(adminId, selectedFolder?.id, selectedSubfolder?.id);

  const breadcrumbItems = [
    {
      key: "status",
      label: "Approved Submissions",
      show: true,
    },
    {
      key: "folder",
      label: selectedFolder
        ? selectedFolder.folderName ||
          selectedFolder.folderId ||
          selectedFolder.id
        : "",
      show: !!selectedFolder,
    },
    {
      key: "subfolder",
      label: selectedSubfolder ? selectedSubfolder.subfolderName : "",
      show: !!selectedSubfolder,
    },
  ];

  const handleBreadcrumbClick = (idx) => {
    setActiveBreadcrumb(idx);
    if (idx === 0) {
      setSelectedFolder(null);
      setSelectedSubfolder(null);
    } else if (idx === 1) {
      setSelectedSubfolder(null);
    } else if (idx === 2) {
      // No change needed, already at subfolder level
    }
  };

  const handleFolderSelectInternal = (folder) => {
    setSelectedFolder(folder);
    setSelectedSubfolder(null);
    setActiveBreadcrumb(1);
  };

  const handleSubfolderSelectInternal = (subfolder) => {
    setSelectedSubfolder(subfolder);
    setActiveBreadcrumb(2);
  };

  const handleDownloadClick = async () => {
    if (!selectedFolder) return;
    setIsDownloading(true);
    try {
      await downloadApprovedFolderAsZip(selectedFolder, adminId);
    } catch (error) {
      console.error("Download failed:", error);
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <BreadcrumbNavigation
        breadcrumbItems={breadcrumbItems}
        activeIndex={activeBreadcrumb}
        onBreadcrumbClick={handleBreadcrumbClick}
      />
      <AnimatedText text="Approved Submissions" />
      {foldersLoading ? (
        <CircularProgress />
      ) : foldersError ? (
        <Alert severity="error">Failed to load approved folders.</Alert>
      ) : !approvedFolders || approvedFolders.length === 0 ? (
        <Typography>No approved submissions found.</Typography>
      ) : selectedFolder ? (
        <>
          <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
            <Typography variant="h6" gutterBottom sx={{ flexGrow: 1 }}>
              {selectedFolder.folderName || selectedFolder.id}
            </Typography>
            <Button
              variant="contained"
              color="primary"
              onClick={handleDownloadClick}
              disabled={isDownloading}
            >
              {isDownloading ? "Downloading..." : "Download"}
            </Button>
          </Box>
          <Grid container spacing={2} sx={{ justifyContent: "center" }}>
            <Grid item xs={4}>
              {subfoldersLoading ? (
                <CircularProgress />
              ) : subfoldersError ? (
                <Alert severity="error">Failed to load subfolders.</Alert>
              ) : (
                <SubfolderList
                  subfolders={subfolders || []}
                  selectedSubfolder={selectedSubfolder}
                  onSubfolderSelect={handleSubfolderSelectInternal}
                />
              )}
            </Grid>
            <Grid item xs={8}>
              {filesLoading ? (
                <CircularProgress />
              ) : filesError ? (
                <Alert severity="error">Failed to load files.</Alert>
              ) : selectedSubfolder ? (
                <FileList files={files || []} onViewFile={handleViewFile} />
              ) : (
                <Typography>Select a subfolder to view files.</Typography>
              )}
            </Grid>
          </Grid>
        </>
      ) : (
        <Grid container spacing={2} sx={{ justifyContent: "center" }}>
          {approvedFolders.map((folder) => (
            <Grid item key={folder.id} xs={6} sm={4} md={3} lg={2}>
              <Box
                onClick={() => handleFolderSelectInternal(folder)}
                sx={{
                  cursor: "pointer",
                  p: 2,
                  borderRadius: 2,
                  backgroundColor:
                    selectedFolder && selectedFolder.id === folder.id
                      ? "rgba(255, 255, 255, 0.1)"
                      : "#121212",
                  boxShadow:
                    selectedFolder && selectedFolder.id === folder.id
                      ? "0 0 10px 2px rgba(11, 107, 203, 0.7)"
                      : "none",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  transition: "background-color 0.3s, box-shadow 0.3s",
                  "&:hover": {
                    backgroundColor: "rgba(255, 255, 255, 0.15)",
                    boxShadow: "0 0 10px 2px rgba(255, 255, 255, 0.09)",
                  },
                }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="48"
                  height="48"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  style={{ marginBottom: 8 }}
                >
                  <path d="M3 7v10a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-6l-2-2H5a2 2 0 0 0-2 2z" />
                </svg>
                <Typography
                  variant="body1"
                  sx={{
                    color: "white",
                    fontWeight: 600,
                    textAlign: "center",
                    wordBreak: "break-word",
                  }}
                >
                  {folder.folderName || folder.id}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      )}
      {previewFile && (
        <FilePreview
          open={Boolean(previewFile)}
          onClose={handleClosePreview}
          file={previewFile}
          onToggleSummary={handleToggleSummary}
          summaryOpen={summaryOpen}
        />
      )}
      <SummaryDrawer
        open={summaryOpen}
        onClose={handleToggleSummary}
        summary={previewFile?.summary}
      />
    </Box>
  );
}

export default ApprovedSubmission;
